import React, { useState } from 'react';
import { useDeepCompareEffect } from 'ahooks';
import FileList, { IFileListProps } from '@gp/components/FileList';
import FileItemActions from './components/FileItemActions';
import useCOS from '@gp/hooks/useCos/useCOS';
import { getBIDSTencentQCouldMassage } from '@gp/services/global';
import { getBasePath } from '@/utils/utils';
import {
  openTabPage,
  downloadFileByLinker,
  reportBtnOperationLog,
} from '@gp/utils/common';
// @ts-ignore
import { copyContent } from '@gp/utils/utils';
import { getFileKey } from '@gp/components/FileList/utils';
import { COMMON_IMAGE_SUFFIXES } from '@gp/constants/const';
import { ENV } from '@home/_config/config';
import type { IBaseProps } from '@home/types/common';
import type { IRecord } from '@gp/types/global';
import type { IFile } from '@gp/components/FileList/types';

interface ICOSFileListProps extends IBaseProps, IFileListProps {
  // 预览类型，不同的预览类型 对应 不同的预览页面
  domain: 'milestone_materials' | 'updates_materials' | (string & {});
  // 预览页面url的查询参数对象
  urlSearchParams?: IRecord<string | number>;
  // cos的配置，主要是获取cos的基础配置的服务，比如Region、Bucket、临时token这些信息
  cosOptions?: IUseCOSOptions;
  // 打点配置
  logKeys?: {
    // 预览打点的key
    previewLogKey: string;
    // 下载打点的key
    downloadLogKey: string;
    // 分享打点的key
    shareLogKey: string;
  };
}

const COSFileList: React.FC<ICOSFileListProps> = (props) => {
  // 全球加速，预发布和正式
  const controlledEnv = ['pre', 'pro'];
  const isUseAccelerate = controlledEnv.includes(ENV);

  const {
    domain = 'milestone_materials',
    urlSearchParams,
    cosOptions,
    logKeys,
    files,
    uploadFolderName,
  } = props;

  const { previewLogKey, downloadLogKey, shareLogKey } = logKeys || {};

  const basePath = getBasePath();

  const DEFAULT_COS_OPTIONS: IUseCOSOptions = {
    useAccelerate: isUseAccelerate,
    baseCOSConfigFetcher: () => getBIDSTencentQCouldMassage(uploadFolderName),
    authorizationFetcher: (_options?: any) =>
      getBIDSTencentQCouldMassage(uploadFolderName),
  };

  const { getObjectUrl, getImagePreviewUrl } = useCOS(
    cosOptions || DEFAULT_COS_OPTIONS,
  );

  const [previewTipsVisibles, setPreviewTipsVisibles] = useState<
  IRecord<true | undefined>
  >({});
  const [copyTipses, setCopyTipses] = useState<IRecord<string>>({});
  const [copyTipsVisibles, setCopyTipsVisibles] = useState<
  IRecord<true | undefined>
  >({});

  const getPreviewerUrl = async (
    path?: string,
    name?: string,
    format?: string,
  ): Promise<string> => {
    if (COMMON_IMAGE_SUFFIXES.includes(format || '')) {
      const { url: imagePreviewUrl } = await getImagePreviewUrl({
        key: path || '',
      });
      return imagePreviewUrl;
    }
    const { protocol, host } = window.location;
    const urlSearchParamsTmp = new URLSearchParams({
      ...urlSearchParams,
      // 预览类型，用于区分进入对应的预览页
      previewer_type: domain,
      // 预览文档的COS存储path
      doc_key: path || '',
      // 预览文档的名称
      doc_name: name || '',
      // 预览文档的文件后缀
      doc_suffix: format || '',
      // upload_folder_name: uploadFolderName || '',
    });
    return `${protocol}//${host}${basePath}/g-pop/DocPreviewer?${urlSearchParamsTmp.toString()}`;
  };

  const handlePreview = async (file?: IFile | null) => {
    const { path, name, format } = (file || {}) as any;
    const url = await getPreviewerUrl(path, name, format);
    reportBtnOperationLog(previewLogKey, { extInfo: path });
    openTabPage(url);
  };

  const handleDownload = async (file?: IFile | null) => {
    const { path } = (file || {}) as any;
    if (!path) return;
    const fileKey = getFileKey(file);
    try {
      const { downloadUrl } = await getObjectUrl({ key: path });
      downloadFileByLinker(`${downloadUrl};filename=${fileKey}`, 'true');
    } catch (exception) {
      console.warn(exception);
    }
    reportBtnOperationLog(downloadLogKey, { extInfo: path });
  };

  const handleShare = async (file?: IFile | null) => {
    const fileKey = getFileKey(file);
    const { path, name, format } = (file || {}) as any;
    const url = await getPreviewerUrl(path, name, format);
    const copyRes = copyContent(url);
    reportBtnOperationLog(shareLogKey, { extInfo: path });
    if (copyRes === 1) {
      const copyTipsesTmp = {
        ...copyTipses,
        [fileKey]: 'Copy successfully!',
      };
      const copyTipsVisiblesTmp = {
        ...copyTipsVisibles,
        [fileKey]: true as true,
      };
      setCopyTipses(copyTipsesTmp);
      setCopyTipsVisibles(copyTipsVisiblesTmp);
    } else {
    }
  };

  const renderActions = (data?: IFile | null) => {
    const key = getFileKey(data);
    const previewTipsVisible = previewTipsVisibles[key];
    const copyTips = copyTipses[key];
    const copyTipsVisible = copyTipsVisibles[key];

    return (
      <FileItemActions
        previewTipsVisible={previewTipsVisible}
        copyTips={copyTips}
        copyTipsVisible={copyTipsVisible}
        onPreview={() => {
          handlePreview(data);
        }}
        onDownload={() => {
          handleDownload(data);
        }}
        onShare={() => {
          handleShare(data);
        }}
      />
    );
  };

  useDeepCompareEffect(() => {
    if (!Array.isArray(files) || !files.length) return;

    const previewTipsVisiblesTmp: IRecord<true | undefined> = {};
    const copyTipsesTmp: IRecord<string> = {};
    const copyTipsVisiblesTmp: IRecord<true | undefined> = {};

    files.forEach((file) => {
      const fileKey = getFileKey(file);
      previewTipsVisiblesTmp[fileKey] = undefined;
      copyTipsesTmp[fileKey] = 'Copy Link';
      copyTipsVisiblesTmp[fileKey] = undefined;
    });

    setCopyTipses(copyTipsesTmp);
    setCopyTipsVisibles(copyTipsVisiblesTmp);

    return () => {
      setCopyTipses({});
      setCopyTipsVisibles({});
    };
  }, [files]);

  return (
    <FileList
      renderActions={renderActions}
      files={files}
      onFileNameClick={(file?: IFile | null) => {
        handlePreview?.(file);
      }}
      onFileNameMouseEnter={(file?: IFile | null) => {
        const fileKey = getFileKey(file);
        const previewTipsVisiblesTmp = {
          ...previewTipsVisibles,
          [fileKey]: true as true,
        };
        setPreviewTipsVisibles(previewTipsVisiblesTmp);
      }}
      onFileNameMouseLeave={(file?: IFile | null) => {
        const fileKey = getFileKey(file);
        const previewTipsVisiblesTmp = {
          ...previewTipsVisibles,
          [fileKey]: undefined,
        };
        setPreviewTipsVisibles(previewTipsVisiblesTmp);
      }}
      onFileItemMouseLeave={(file?: IFile | null) => {
        const fileKey = getFileKey(file);
        const copyTipsesTmp = {
          ...copyTipses,
          [fileKey]: 'Copy Link',
        };
        const copyTipsVisiblesTmp = {
          ...copyTipsVisibles,
          [fileKey]: undefined,
        };
        setCopyTipses(copyTipsesTmp);
        setCopyTipsVisibles(copyTipsVisiblesTmp);
      }}
    />
  );
};

export default COSFileList;
